
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'
// @ts-ignore
import IMask from 'imask'
// @ts-ignore
import VueSlider from 'vue-slider-component'
import { debounce } from '~/lib/utils/utils'
import ProLabel from '~/components/common/freemium/ProLabel.vue'

const numberNormalize = (numStr: string) => Number(numStr.replace(/\D/g, '').trim())

@Component({
  name: 'RangeFilterBar',
  components: {
    VueSlider,
    ProLabel
  }
})
export default class RangeFilterBar extends Vue {
  @Prop({ type: Object, required: true }) filterSpec!: any
  @Prop({ type: String, required: true }) maskScheme!: string
  @Prop({ type: String }) title!: string
  @Prop({ type: Boolean, default: false }) isProFeature!: boolean
  @Prop({ type: String }) proReferer!: string
  @Prop({ type: Number }) possibleMin!: number
  @Prop({ type: Number }) possibleMax!: number
  @Prop({ type: Number }) interval!: number
  @Prop({ required: true }) isPro!: boolean

  $refs!: {
    inputMin: HTMLInputElement,
    inputMax: HTMLInputElement
  }

  appearance = {
    height: 4,
    dotSize: 30,
    dotStyle: {
      backgroundColor: this.$tokens.color_havelock_blue,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      cursor: 'pointer'
    },
    railStyle: {
      backgroundColor: this.$tokens.color_geyser
    },
    processStyle: {
      backgroundColor: !this.isPro && this.isProFeature ? this.$tokens.color_geyser : this.$tokens.color_onahau
    }
  }

  mask = {
    min: null,
    max: null
  }

  defineMaskOptions(name: string) {
    const maskOptions = (min: number, max: number, mask: string) => {
      return {
        mask,
        lazy: false,
        signed: false,
        blocks: {
          N: {
            mask: Number,
            min,
            max,
            thousandsSeparator: ','
          }
        }
      }
    }

    const opts = {
      min: [0, this.possibleMax - this.interval, this.maskScheme],
      max: [this.interval, this.possibleMax, this.maskScheme]
    }

    // @ts-ignore
    return maskOptions(opts[name][0], opts[name][1], opts[name][2])
  }

  mounted() {
    if (this.possibleMin === 0 && this.possibleMax === 1) { return }

    if (this.filterSpec.min === 0) {
      this.filterSpec.min = this.possibleMin
    }

    if (this.filterSpec.max === 0) {
      this.filterSpec.max = this.possibleMax
    }

    ['min', 'max'].forEach((inputName: 'min' | 'max') => {
      const input = this.$refs['input' + this.$capitalize(inputName)]
      this.mask[inputName] = IMask(input, this.defineMaskOptions(inputName))
      this.setMaskValue(inputName, this.filterSpec[inputName] || this['possible' + this.$capitalize(inputName)])

      this.mask[inputName].on('accept', () => {
        const value = numberNormalize(this.mask[inputName].unmaskedValue)
        this.setModelValue(inputName, value)
      })

      input.addEventListener('blur', () => {
        this.setMaskValue(inputName, this.filterSpec[inputName] || this['possible' + this.$capitalize(inputName)])
      })
    })
  }

  get isDisabled() {
    return (!this.isPro && this.isProFeature) || (this.possibleMin === 0 && this.possibleMax === 1)
  }

  @Watch('filterSpec.min')
  filterSpecMinChangeHandler(data: number) {
    if (!data) {
      this.$emit('input', { min: this.possibleMin, max: this.filterSpec.max })
      this.setMaskValue('min', this.possibleMin)
    }
  }

  @Watch('filterSpec.max')
  filterSpecMaxChangeHandler(data: number) {
    if (!data) {
      this.$emit('input', { min: this.filterSpec.min, max: this.possibleMax })
      this.setMaskValue('max', this.possibleMax)
    }
  }

  onSliderChange(data: number[]) {
    debounce(() => {
      this.$emit('input', { min: data[0], max: data[1] })
      this.$nextTick(() => {
        this.setMaskValue('min', this.filterSpec.min)
        this.setMaskValue('max', this.filterSpec.max)
      })
    }, 300)
  }

  setMaskValue(inputName: 'min' | 'max', value: number) {
    this.mask[inputName].unmaskedValue = value.toString()
    this.mask[inputName].updateControl()
  }

  setModelValue(inputName: 'min' | 'max', value) {
    const validate = {
      min: ({ possibleMin, max, interval, value }) => {
        if (value <= possibleMin) { return possibleMin }
        if (value >= max) { return max - interval }
        return value
      },
      max: ({ min, possibleMax, interval, value }) => {
        if (value >= possibleMax) { return possibleMax }
        if (value <= (min + interval)) { return min + interval }
        return value
      }
    }

    const newValue = validate[inputName]({
      min: this.filterSpec.min,
      possibleMin: this.possibleMin,
      max: this.filterSpec.max,
      possibleMax: this.possibleMax,
      interval: this.interval,
      value
    })

    const props = {
      min: this.filterSpec.min,
      max: this.filterSpec.max
    }

    props[inputName] = newValue

    this.$emit('input', props)
  }
}
